/**
 * Base service class for Custom Backend operations
 * Contains shared utilities and common functionality
 */

import { createDomainLogger } from "../../../logger";
import { buildQueryString, getCommonHeaders } from "../shared";
import { IOptionsRequest } from "../../../models";
import { ICustomConfig } from "../interface";

export abstract class BaseCustomService {
  protected baseUrl: string;
  protected config: ICustomConfig;
  protected timeout: number = 5000;
  protected logger: ReturnType<typeof createDomainLogger>;

  constructor(
    baseUrl: string,
    config: ICustomConfig,
    logger?: ReturnType<typeof createDomainLogger>
  ) {
    this.baseUrl = baseUrl;
    this.config = config;
    this.logger = logger || createDomainLogger("custom", "custom");
  }

  /**
   * Log equivalent curl command for debugging
   */
  private logCurlCommand(
    url: string,
    method: string,
    headers: Headers,
    data?: any
  ): void {
    let curlCommand = `curl -X ${method}`;

    // Add headers
    headers.forEach((value, key) => {
      curlCommand += ` -H "${key}: ${value}"`;
    });

    // Add data for non-GET requests
    if (data && method !== "GET") {
      curlCommand += ` -d '${JSON.stringify(data)}'`;
    }

    // Add URL
    curlCommand += ` "${url}"`;

    this.logger.debug("Curl command:", curlCommand);
  }

  /**
   * Simple API request method
   */
  protected async request(
    endpoint: string,
    method: "GET" | "POST" | "PUT" | "DELETE" = "GET",
    data?: any,
    options?: IOptionsRequest
  ): Promise<any> {
    const url = `${this.baseUrl}${endpoint}`;
    const query = {
      storeId: this.config.storeId,
      ...(options?.params || {}),
    };
    const queryString = buildQueryString(query);
    const headers = getCommonHeaders(this.config);

    // Add custom headers if provided
    if (options?.headers) {
      Object.entries(options.headers).forEach(([key, value]) => {
        headers.append(key, value);
      });
    }

    const requestOptions: RequestInit = {
      method,
      headers,
    };

    if (data && method !== "GET") {
      requestOptions.body = JSON.stringify(data);
    }

    // Log curl command for debugging
    this.logCurlCommand(url + queryString, method, headers, data);

    const response = await fetch(`${url}${queryString}`, requestOptions);

    if (!response.ok) {
      throw new Error(
        `Request failed: ${response.status} ${response.statusText}`
      );
    }

    return response.json();
  }
}
